import { Alert, Box, Button, CircularProgress, Divider, TextField, Typography, alpha, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { IMarketplace } from "../../../interfaces/IMarketplace";
import { useSnackbar } from "../../../../core/contexts/SnackbarContext";
import { useWhatsApp } from "../../../hooks/useWhatsApp";
import { AlterarNumeroContainer, ButtonsContainer, CodigoContainer, ConexaoContainer, QRCodeContainer } from "./styles";
import { useMascara } from "../../../../core/hooks/useMascara";
import { validarTelefone } from "../../../../core/utils/validarTelefone";
import { CheckCircle, QrCode2Outlined, Refresh } from "@mui/icons-material";
import { SecaoConfiguracao } from "../../../../core/components/SecaoConfiguracao";

const QR_CODE_EXPIRY_SECONDS = 60;
const VERDE_WHATSAPP = "#25D366";

interface ConfigurarConexaoWhatsAppProps {
    marketplace: IMarketplace;
    refresh: () => void;
    /** Informa o cabeçalho da página sobre o estado da conexão */
    onStatusChange?: (conectado: boolean | undefined) => void;
}

export const ConfigurarConexaoWhatsApp = ({ marketplace, refresh, onStatusChange }: ConfigurarConexaoWhatsAppProps) => {
    const [tab, setTab] = useState<'qr-code' | 'codigo'>('qr-code');
    const [qrCode, setQrCode] = useState<string | undefined>();
    const [codigo, setCodigo] = useState<string | undefined>();
    const [status, setStatus] = useState<undefined | boolean>()
    const [telefone, setTelefone] = useState<string | undefined>(undefined);
    const [countdown, setCountdown] = useState<number>(QR_CODE_EXPIRY_SECONDS);
    const { mascaraTelefone } = useMascara();
    const theme = useTheme();

    const { gerarQRCode, gerarCodigo, consultarStatus, desconectar, loading: loadingWpp } = useWhatsApp();

    useEffect(() => {
        if (!marketplace?.uuid) return;

        const checkStatus = async () => {
            if (!marketplace?.uuid) return;
            const statusAtual = await consultarStatus(marketplace.uuid);
            setStatus(statusAtual);
        };

        checkStatus();
        const interval = setInterval(checkStatus, 2000);

        return () => clearInterval(interval);
    }, [marketplace?.uuid]); // Removed consultarStatus from dependencies to prevent infinite loop

    // Replica o status para o cabeçalho da página
    useEffect(() => {
        onStatusChange?.(status);
    }, [status]);

    useEffect(() => {
        if (!marketplace?.uuid || status) return;

        const fetchQRCodeOrCodigo = async () => {
            if (!marketplace?.uuid) return;

            if (tab === 'qr-code') {
                const qrCodeBase64 = await gerarQRCode(marketplace.uuid);
                setQrCode(qrCodeBase64);
                setCountdown(QR_CODE_EXPIRY_SECONDS);
            } else if (tab === 'codigo' && telefone && validarTelefone(telefone)) {
                const codigoGerado = await gerarCodigo(marketplace.uuid, telefone);
                if (codigoGerado) setCodigo(codigoGerado);
            }
        };

        fetchQRCodeOrCodigo();
        const interval = setInterval(fetchQRCodeOrCodigo, QR_CODE_EXPIRY_SECONDS * 1000);

        return () => clearInterval(interval);
    }, [marketplace?.uuid, status, tab, telefone]); // Removed function dependencies to prevent infinite loop

    // Countdown timer for QR code tab
    useEffect(() => {
        if (tab !== 'qr-code' || !qrCode || status) return;

        const interval = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) return QR_CODE_EXPIRY_SECONDS;
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [tab, qrCode, status]);

    const notificar = useSnackbar();

    const handleConsultarQRCode = async () => {
        if (!marketplace?.uuid) return;
        if(status) return;

        const qrCodeBase64 = await gerarQRCode(marketplace.uuid);
        setQrCode(qrCodeBase64);
    }

    const handleGerarCodigo = async () => {
        if (!marketplace?.uuid) return;
        if(status) return;

        if (!telefone?.length || !validarTelefone(telefone)) {
            return notificar("Número de telefone inválido", { severity: 'error' })
        }

        const codigoGerado = await gerarCodigo(marketplace.uuid, telefone);
        if (codigoGerado) setCodigo(codigoGerado);
    }

    const handleTab = async (tab: 'qr-code' | 'codigo') => {
        setQrCode(undefined);
        setTelefone(undefined);
        setTab(tab);
    }

    const handleAlterarTelefone = async () => {
        setTelefone(undefined);
        setCodigo(undefined);
    }

    const handleDesconectar = async () => {
        if (!marketplace?.uuid) return;

        const desconectado = await desconectar(marketplace.uuid);
        if (desconectado) {
            setStatus(false);
            refresh();
        }
    }

    useEffect(() => {
        handleConsultarQRCode();
    }, [tab])

    return (
        <SecaoConfiguracao
            titulo="Conexão"
            descricao={status ? "Número conectado à instância" : "Vincule o número do WhatsApp à instância"}
            icone={<QrCode2Outlined />}
            corIcone={status ? VERDE_WHATSAPP : undefined}
            acoes={
                status ? (
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={handleDesconectar}
                        disabled={loadingWpp}
                    >
                        {loadingWpp ? <CircularProgress size={18} /> : 'Desconectar'}
                    </Button>
                ) : tab === 'qr-code' ? (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={handleConsultarQRCode}
                        disabled={loadingWpp}
                    >
                        Novo QR Code
                    </Button>
                ) : undefined
            }
        >
            {status ? (
                <ConexaoContainer
                    sx={{
                        borderRadius: "12px",
                        border: `1px solid ${alpha(VERDE_WHATSAPP, 0.35)}`,
                        backgroundColor: alpha(VERDE_WHATSAPP, 0.08)
                    }}
                >
                    <CheckCircle sx={{ fontSize: 44, mb: 1, color: VERDE_WHATSAPP }} />
                    <Typography variant="subtitle1" fontWeight={600} sx={{ color: VERDE_WHATSAPP }}>
                        Número conectado com sucesso
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
                        Agora seu WhatsApp está pronto para automatizar o seu atendimento.
                    </Typography>
                </ConexaoContainer>
            ) : (
                <>
                    {tab == 'qr-code' ? (
                        <QRCodeContainer>
                            {loadingWpp ? (
                                <CircularProgress size={60} />
                            ) : (
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Leia o QR Code pelo WhatsApp do número que fará o atendimento
                                    </Typography>
                                    {qrCode && (
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: "16px",
                                                backgroundColor: "#FFF",
                                                border: `1px solid ${theme.palette.divider}`,
                                                display: "flex"
                                            }}
                                        >
                                            <img src={qrCode} alt="QR Code do WhatsApp" style={{ width: "260px", height: "260px" }} />
                                        </Box>
                                    )}
                                    {qrCode && (
                                        <Typography variant="caption" sx={{ mt: 1.5, color: 'text.secondary' }}>
                                            Novo QR Code em <strong>{countdown}s</strong>
                                        </Typography>
                                    )}
                                    <Divider
                                        orientation="horizontal"
                                        variant="middle"
                                        flexItem
                                        sx={{ mb: 2, mt: 2 }}
                                    >
                                        <Typography variant="caption" color="text.secondary">ou</Typography>
                                    </Divider>
                                    <Button size="small" onClick={() => handleTab('codigo')}>
                                        Conecte com o número de telefone
                                    </Button>
                                </>
                            )}
                        </QRCodeContainer>
                    ) : (
                        <CodigoContainer>
                            {codigo ? (
                                <>
                                    <AlterarNumeroContainer variant="outlined">
                                        <Typography variant="body2" fontWeight={500}>
                                            {telefone}
                                        </Typography>
                                        <Button size="small" onClick={handleAlterarTelefone}>
                                            Alterar
                                        </Button>
                                    </AlterarNumeroContainer>
                                    <Typography variant="caption" color="text.secondary">
                                        Digite o código abaixo no WhatsApp do número informado
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        fontWeight={600}
                                        sx={{ letterSpacing: "0.25rem", mt: 1 }}
                                    >
                                        {codigo}
                                    </Typography>
                                    <ButtonsContainer>
                                        <Button
                                            disabled={loadingWpp}
                                            fullWidth
                                            onClick={() => handleTab('qr-code')}
                                            sx={{ mt: "2rem" }}
                                        >
                                            Conectar com QR Code
                                        </Button>
                                    </ButtonsContainer>
                                </>
                            ) : (
                                <>
                                    <Alert severity="info" variant="outlined" sx={{ borderRadius: "12px", width: "100%", mb: 2 }}>
                                        <Typography variant="caption">
                                            Informe o número que fará o atendimento para gerar um código de 8 dígitos.
                                        </Typography>
                                    </Alert>
                                    <TextField
                                        label="Telefone"
                                        size="small"
                                        fullWidth
                                        placeholder="(41) 99999-9999"
                                        sx={{ mb: 2 }}
                                        value={telefone ?? ""}
                                        onChange={(e) => setTelefone(mascaraTelefone(e.target.value))}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <ButtonsContainer>
                                        <Button
                                            disabled={loadingWpp}
                                            fullWidth
                                            variant="outlined"
                                            onClick={() => handleTab('qr-code')}
                                            sx={{ height: '44px' }}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant={loadingWpp ? 'outlined' : "contained"}
                                            onClick={() => handleGerarCodigo()}
                                            disabled={loadingWpp}
                                            sx={{ height: '44px' }}
                                        >
                                            {
                                                loadingWpp
                                                    ? <CircularProgress size={15} />
                                                    : 'Gerar'
                                            }
                                        </Button>
                                    </ButtonsContainer>
                                </>
                            )
                            }
                        </CodigoContainer >
                    )}
                </>
            )}
        </SecaoConfiguracao>
    );
}
